/**
 * Core Contact Information Section Component
 * 
 * Base contact info component that can be themed and customized.
 * Displays company contact details in a clean, organized layout.
 */

'use client';

import type { ReactNode } from 'react';

export interface ContactInfo {
  type: 'email' | 'phone' | 'address' | 'hours' | 'custom';
  label: string;
  value: string;
  icon?: ReactNode;
  href?: string;
}

export interface ContactInformationSectionProps {
  title: string;
  subtitle?: string;
  contactInfo: ContactInfo[];
  className?: string;
  renderContactItem?: (info: ContactInfo) => ReactNode;
}

/**
 * Core Contact Information Section Component
 * Clean, flexible base that themes can extend
 */
export function ContactInformationSection({
  title,
  subtitle,
  contactInfo,
  className = '',
  renderContactItem,
}: ContactInformationSectionProps) {
  
  const defaultRenderItem = (info: ContactInfo) => {
    const content = (
      <>
        {info.icon && <div className="contact-info-icon">{info.icon}</div>}
        <div className="contact-info-content">
          <p className="contact-info-label">{info.label}</p>
          {info.href ? (
            <a href={info.href} className="contact-info-value-link">
              {info.value}
            </a>
          ) : (
            <p className="contact-info-value">{info.value}</p>
          )}
        </div>
      </>
    );

    return (
      <div key={`${info.type}-${info.label}`} className="contact-info-item">
        {content}
      </div>
    );
  };

  return (
    <section className={`contact-information-section ${className}`}>
      <div className="contact-information-header">
        <h2 className="contact-information-title">{title}</h2>
        {subtitle && <p className="contact-information-subtitle">{subtitle}</p>}
      </div>

      <div className="contact-information-grid">
        {contactInfo.map(info => 
          renderContactItem ? renderContactItem(info) : defaultRenderItem(info)
        )}
      </div>
    </section>
  );
}

// Default export for backward compatibility
export default ContactInformationSection;

